/* SWITZ — product card image carousel.
 *
 * Everything is delegated from `document`, so Shopify's facet re-render, the
 * theme editor reloading the section, and pagination all keep working without
 * re-initialising anything and without stacking duplicate listeners.
 *
 * Per-card state lives on the element itself (el.__switz), so each card is
 * independent and a re-rendered card simply starts fresh.
 *
 * Two image layers:
 *   A — the product's original Shopify image, with its full srcset. NEVER
 *       written to, so returning home is always a crossfade back to the real
 *       default image rather than whatever happened to be loaded last.
 *   B — the preview layer. Holds whichever extra image is being shown.
 *
 * The next image is fetched on a detached Image() first and only swapped into
 * layer B once it has loaded, so it paints from cache in one go.
 *
 * Do not reintroduce img.decode() here: on a detached image its promise can
 * never settle, which silently strands the card with busy = true and kills
 * every later click.
 *
 * Mouse: hover reveals arrows (CSS), leaving the card resets to image 1.
 * Touch: swipe left/right or tap the arrows; the chosen image stays until the
 *        customer moves to another card or scrolls this one out of view.
 */
(function () {
  'use strict';

  var CAROUSEL = '[data-switz-carousel]';
  var STUCK_MS = 5000;
  var SWIPE_MIN = 30;

  /* Can this device do touch at all?
   *
   * `(hover: none)` alone is not dependable — several Android browsers and
   * iPadOS report `hover: hover` on a touchscreen, which is what left real
   * phones with no visible arrows. Coarse pointers and maxTouchPoints cover
   * those cases.
   */
  var touchCapable =
    !!(window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) ||
    navigator.maxTouchPoints > 0;

  /* Which input the customer last actually used.
   *
   * A touchscreen laptop is both things at once, so deciding behaviour from the
   * device alone would rob mouse users on those machines of reset-on-leave.
   * This tracks the live input instead and switches behaviour with it.
   */
  var lastInputWasTouch = touchCapable;
  document.addEventListener(
    'pointerdown',
    function (event) {
      if (event.pointerType) {
        lastInputWasTouch = event.pointerType === 'touch' || event.pointerType === 'pen';
      }
    },
    true
  );

  function getState(el) {
    if (el.__switz) return el.__switz;

    var tag = el.querySelector('script[data-switz-media]');
    var urls = [];
    if (tag) {
      try {
        urls = JSON.parse(tag.textContent) || [];
      } catch (e) {
        urls = [];
      }
    }
    if (urls.length < 2) return null;

    var a = el.querySelector('.switz-card__img--a');
    var b = el.querySelector('.switz-card__img--b');
    if (!a || !b) return null;

    el.__switz = {
      urls: urls,
      index: 0,
      a: a,
      b: b,
      dots: el.querySelectorAll('.switz-card__dot'),
      live: el.querySelector('[data-switz-live]'),
      busy: false,
      touchX: null,
      touchY: null
    };
    return el.__switz;
  }

  function preload(url) {
    if (!url) return;
    var img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  function paintDots(state) {
    for (var i = 0; i < state.dots.length; i++) {
      state.dots[i].classList.toggle('is-on', i === state.index);
    }
  }

  function announce(state) {
    if (!state.live) return;
    state.live.textContent =
      state.index === 0 ? '' : 'Image ' + (state.index + 1) + ' of ' + state.urls.length;
  }

  function warmNeighbours(state) {
    var total = state.urls.length;
    preload(state.urls[(state.index + 1) % total]);
    preload(state.urls[(state.index - 1 + total) % total]);
  }

  function goHome(el, state) {
    el.classList.remove('is-b');
    state.index = 0;
    paintDots(state);
    announce(state);
  }

  function goTo(el, nextIndex) {
    var state = getState(el);
    if (!state || state.busy) return;

    var total = state.urls.length;
    var target = ((nextIndex % total) + total) % total;
    if (target === state.index) return;

    // Layer A already holds image 1, so going home needs no network at all.
    if (target === 0) {
      goHome(el, state);
      warmNeighbours(state);
      return;
    }

    var url = state.urls[target];
    state.busy = true;

    // A stalled or failed image must never leave the card permanently frozen.
    var guard = window.setTimeout(function () {
      state.busy = false;
    }, STUCK_MS);

    var settle = function (ok) {
      window.clearTimeout(guard);
      if (ok) {
        state.b.src = url;
        el.classList.add('is-b');
        state.index = target;
        paintDots(state);
        announce(state);
      }
      state.busy = false;
      if (ok) warmNeighbours(state);
    };

    var pre = new Image();
    pre.decoding = 'async';
    pre.onload = function () {
      settle(true);
    };
    pre.onerror = function () {
      settle(false);
    };
    pre.src = url;
  }

  function reset(el) {
    var state = el.__switz;
    if (!state || state.index === 0) return;
    state.busy = false;
    goHome(el, state);
  }

  function resetAllExcept(keep) {
    var all = document.querySelectorAll(CAROUSEL);
    for (var i = 0; i < all.length; i++) {
      if (all[i] !== keep) reset(all[i]);
    }
  }

  /* ---------- Arrows ---------- */
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('[data-switz-prev], [data-switz-next]');
      if (!button) return;

      // The whole card is a link (the title's ::after overlay), so the arrow
      // must not let this click reach it.
      event.preventDefault();
      event.stopPropagation();

      var el = button.closest(CAROUSEL);
      if (!el) return;

      var state = getState(el);
      if (!state) return;

      // On touch there is no mouse-leave, so moving to a card tidies the others.
      if (lastInputWasTouch) resetAllExcept(el);
      goTo(el, state.index + (button.hasAttribute('data-switz-next') ? 1 : -1));

      // A real pointer click leaves the button focused, and :focus-within would
      // then keep the arrows lit on a card the customer is no longer hovering.
      // event.detail is 0 for keyboard activation, so keyboard focus survives.
      if (event.detail > 0 && button.blur) button.blur();
    },
    true
  );

  /* ---------- Mouse: warm the next image, reset on leave ---------- */
  document.addEventListener('mouseover', function (event) {
    var el = event.target.closest && event.target.closest(CAROUSEL);
    if (!el) return;
    var state = getState(el);
    if (state && state.index === 0) preload(state.urls[1]);
  });

  document.addEventListener('mouseout', function (event) {
    // Touch taps emit synthetic mouse events; resetting on those would yank the
    // image away while the customer is still looking at it.
    if (lastInputWasTouch) return;
    var el = event.target.closest && event.target.closest(CAROUSEL);
    if (!el) return;
    // mouseout also fires moving between children; only act on a real exit.
    var to = event.relatedTarget;
    if (to && el.contains(to)) return;
    reset(el);
  });

  /* ---------- Touch: swipe ---------- */
  document.addEventListener(
    'touchstart',
    function (event) {
      var el = event.target.closest && event.target.closest(CAROUSEL);
      if (!el) return;
      var state = getState(el);
      if (!state) return;
      var touch = event.changedTouches[0];
      state.touchX = touch.clientX;
      state.touchY = touch.clientY;
      preload(state.urls[(state.index + 1) % state.urls.length]);
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    function (event) {
      var el = event.target.closest && event.target.closest(CAROUSEL);
      if (!el) return;
      var state = getState(el);
      if (!state || state.touchX === null) return;

      var touch = event.changedTouches[0];
      var dx = touch.clientX - state.touchX;
      var dy = touch.clientY - state.touchY;
      state.touchX = null;
      state.touchY = null;

      // Ignore vertical scrolls and stray taps.
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;

      resetAllExcept(el);
      goTo(el, state.index + (dx < 0 ? 1 : -1));
    },
    { passive: true }
  );

  /* ---------- Reset a card once it is well out of view ---------- */
  if (touchCapable && 'IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) reset(entry.target);
        });
      },
      { threshold: 0 }
    );

    var observeAll = function () {
      var all = document.querySelectorAll(CAROUSEL);
      for (var i = 0; i < all.length; i++) {
        if (!all[i].__switzObserved) {
          all[i].__switzObserved = true;
          observer.observe(all[i]);
        }
      }
    };

    observeAll();

    // Facet filtering swaps the grid's DOM wholesale; pick up the new cards.
    var host = document.getElementById('ProductGridContainer') || document.body;
    new MutationObserver(observeAll).observe(host, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', observeAll);
  }
})();
